/**
 * Partner Portal — Quote Leads / Referrals page (Slice D2 — Feb 2026 July).
 *
 * Franchisees see ONLY their own leads (server enforces via
 * partner_id from the JWT). The page pairs a status board summary at
 * the top with a filterable list and a slide-in drawer for editing an
 * individual lead's lifecycle.
 *
 * Status lifecycle (partner-side allowed transitions):
 *   quote_requested   → partner_notified | customer_contacted | quote_sent | declined
 *   partner_notified  → customer_contacted | quote_sent | declined
 *   customer_contacted→ quote_sent | declined | accepted
 *   quote_sent        → accepted | declined | completed
 *   accepted          → completed | declined
 *
 * AWARE tracks referral status only — never the partner's actual
 * quote. `estimated_value_cents` is optional; when the franchisee
 * flips to `quote_sent` without filling it we show a soft yellow nudge.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Inbox, ChevronRight, User, Mail, Phone, MapPin,
  ClipboardList, AlertCircle, CheckCircle2, XCircle, Send,
  DollarSign, Info, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { TankCleanContext } from "@/components/TankCleanContext";
import { useSEO } from "@/lib/useSEO";
import { useNavigate, useParams, Link } from "react-router-dom";
import { PartnerShell } from "@/pages/partner/Dashboard";

// ── Status metadata ─────────────────────────────────────────────
// Feb 2026 · Task 3 — Aligned labels with the user-facing CRM ladder
// (New → Contacted → Quote Sent → Won → Lost → Completed) while
// keeping the backend status keys stable.
const STATUS_META = {
  quote_requested:    { label: "New",             tone: "bg-cyan-50 text-cyan-800 border-cyan-200",       icon: Inbox },
  partner_notified:   { label: "New · Notified",  tone: "bg-blue-50 text-blue-800 border-blue-200",       icon: Send  },
  customer_contacted: { label: "Contacted",       tone: "bg-indigo-50 text-indigo-800 border-indigo-200", icon: Phone },
  quote_sent:         { label: "Quote sent",      tone: "bg-amber-50 text-amber-800 border-amber-200",    icon: ClipboardList },
  accepted:           { label: "Won",             tone: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  declined:           { label: "Lost",            tone: "bg-rose-50 text-rose-800 border-rose-200",       icon: XCircle },
  completed:          { label: "Completed",      tone: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: CheckCircle2 },
};

const STATUS_ORDER = [
  "quote_requested", "partner_notified", "customer_contacted",
  "quote_sent", "accepted", "declined", "completed",
];

function StatusPill({ status, small = false }) {
  const meta = STATUS_META[status] || { label: status, tone: "bg-slate-100 text-slate-700 border-slate-200", icon: Inbox };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 border font-medium rounded-full ${meta.tone} ${small ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`}>
      <Icon className={small ? "w-3 h-3" : "w-3.5 h-3.5"} /> {meta.label}
    </span>
  );
}

// ── Format helpers ───────────────────────────────────────────────
const fmtCurrency = (cents) =>
  cents == null || cents === "" ? "" :
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(cents) / 100);

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-AU") : "—";

// ── Page ─────────────────────────────────────────────────────────
export default function PartnerQuoteLeadsPage() {
  useSEO({ title: "Quote Requests — Aware Partner Portal", noindex: true, includeBaseline: false });

  const { leadId } = useParams();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(leadId || null);
  const [deepLinked, setDeepLinked] = useState(null);
  const [deepLinkError, setDeepLinkError] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/partner/quote-leads");
      setLeads(data.leads || []);
      setCounts(data.counts || {});
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load quote leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sep 2026 — deep link from the notification bell (/partner/leads/:leadId).
  // The list only returns recent leads, so fall back to the single-lead
  // endpoint. That endpoint 404s for another partner's lead, which we
  // surface as a friendly state rather than a blank page.
  useEffect(() => {
    setSelectedId(leadId || null);
    setDeepLinkError(null);
    setDeepLinked(null);
    if (!leadId || loading) return;
    if (leads.some((l) => l.id === leadId)) return;
    let cancelled = false;
    api.get(`/partner/quote-leads/${leadId}`)
      .then(({ data }) => { if (!cancelled) setDeepLinked(data); })
      .catch(() => {
        if (!cancelled) {
          setDeepLinkError(
            "We couldn't open that quote request. It may have been removed, or it is no longer allocated to your account.",
          );
        }
      });
    return () => { cancelled = true; };
  }, [leadId, loading, leads]);

  const filtered = useMemo(() => {
    if (filter === "all") return leads;
    return leads.filter((l) => l.status === filter);
  }, [leads, filter]);

  const selected = useMemo(
    () => leads.find((l) => l.id === selectedId) || (deepLinked?.id === selectedId ? deepLinked : null),
    [leads, selectedId, deepLinked],
  );

  // Keep the URL in step with the open drawer so the browser back button
  // returns the partner to the list rather than out of the portal.
  const openLead = (id) => navigate(`/partner/leads/${id}`);
  const closeLead = () => navigate("/partner/leads");

  return (
    <PartnerShell active="quote requests">
      <div className="space-y-6" data-testid="partner-leads-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Referrals</div>
            <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">Quote requests</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Customer-initiated requests from their AWARE water-test recommendations.
              Contact the customer directly and prepare your own quotation using your
              own tools — we only track referral status here.
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="partner-leads-refresh" className="text-xs">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Refresh
          </Button>
        </div>

        {deepLinkError && (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 p-4 text-sm"
            data-testid="partner-leads-deeplink-error"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-none" />
              <div>
                <div className="font-semibold">Quote request unavailable</div>
                <p className="mt-1">{deepLinkError}</p>
                <Link
                  to="/partner/leads"
                  className="mt-2 inline-block font-semibold underline underline-offset-2"
                  data-testid="partner-leads-deeplink-back"
                >
                  Back to all quote requests
                </Link>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-3 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-none" /> {error}
          </div>
        )}

        {/* Summary board */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          <button
            data-testid="partner-leads-filter-all"
            onClick={() => setFilter("all")}
            className={`text-left rounded-xl border p-3 transition-all ${
              filter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className={`text-[10px] uppercase tracking-wider font-semibold ${filter === "all" ? "text-slate-300" : "text-slate-500"}`}>All</div>
            <div className="text-2xl font-bold mt-1">{total}</div>
          </button>
          {STATUS_ORDER.map((s) => {
            const meta = STATUS_META[s];
            const active = filter === s;
            return (
              <button
                key={s}
                data-testid={`partner-leads-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`text-left rounded-xl border p-3 transition-all ${
                  active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${active ? "text-slate-300" : "text-slate-500"}`}>{meta.label}</div>
                <div className="text-2xl font-bold mt-1">{counts[s] || 0}</div>
              </button>
            );
          })}
        </div>

        {/* Lead list */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading leads…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="partner-leads-empty">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              {filter === "all"
                ? "No quote requests yet. When a customer clicks Request a Quote from their AWARE report, it'll show up here."
                : `No leads currently in "${STATUS_META[filter]?.label}".`}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((lead) => (
                <li key={lead.id}>
                  <button
                    data-testid={`partner-lead-row-${lead.id}`}
                    onClick={() => openLead(lead.id)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-medium text-slate-900 truncate">{lead.customer_name || "Unknown customer"}</div>
                        <StatusPill status={lead.status} small />
                      </div>
                      <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.property_address || "—"}</span>
                        <span className="inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" />{lead.package_name || "—"}</span>
                        <span>{fmtDate(lead.created_at)}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-none" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={closeLead}
          onSaved={(updated) => {
            setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
            // Recount after update
            load();
          }}
        />
      )}
    </PartnerShell>
  );
}

// ── Drawer for editing a lead ─────────────────────────────────────
function LeadDrawer({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState(lead.status);
  const [quoteNumber, setQuoteNumber] = useState(lead.internal_quote_number || "");
  const [valueDollars, setValueDollars] = useState(
    lead.estimated_value_cents != null ? String(Number(lead.estimated_value_cents) / 100) : "",
  );
  const [notes, setNotes] = useState(lead.partner_notes || "");
  const [outcome, setOutcome] = useState(lead.quote_outcome || "");
  const [historyNote, setHistoryNote] = useState("");
  const [saving, setSaving] = useState(false);

  const nudgeQuoteValue = status === "quote_sent" && !valueDollars;

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        status,
        internal_quote_number: quoteNumber || "",
        estimated_value_cents: valueDollars ? Math.round(Number(valueDollars) * 100) : null,
        partner_notes: notes,
        quote_outcome: outcome,
        note: historyNote || null,
      };
      const { data } = await api.patch(`/partner/quote-leads/${lead.id}`, payload);
      toast.success(`Status updated → ${STATUS_META[data.status]?.label || data.status}`);
      onSaved(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update lead.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="partner-lead-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Quote lead</div>
            <h2 className="text-lg font-semibold text-slate-900 truncate">{lead.customer_name || "Unknown customer"}</h2>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <StatusPill status={lead.status} small />
              <span className="text-xs text-slate-500">Ref {lead.proposal_reference || lead.id}</span>
            </div>
          </div>
          <button data-testid="partner-lead-drawer-close" onClick={onClose} className="text-slate-500 hover:text-slate-900 text-sm">Close</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer block */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm">
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-500" /><span className="font-medium">{lead.customer_name || "Not provided"}</span></div>
            {lead.customer_email && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                <a href={`mailto:${lead.customer_email}`} className="text-cyan-700 hover:underline">{lead.customer_email}</a>
              </div>
            )}
            {lead.customer_phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-500" />
                <a href={`tel:${lead.customer_phone}`} className="text-cyan-700 hover:underline">{lead.customer_phone}</a>
              </div>
            )}
            {lead.property_address && (
              <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-slate-500" />{lead.property_address}</div>
            )}
            <div className="flex items-center gap-2 text-slate-700"><ClipboardList className="w-4 h-4 text-slate-500" />{lead.package_name || "—"}</div>
          </section>

          {/* Recommended actions */}
          {(lead.recommended_actions || []).length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Recommended actions from the report</h3>
              <ul className="space-y-1 text-sm text-slate-700 pl-1">
                {lead.recommended_actions.map((a, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full mt-2 flex-none" />
                    <span>{typeof a === "string" ? a : (a.name || a.title || "Recommended action")}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2">
                <TankCleanContext recommended={lead.tank_clean_recommended} context={lead.tank_clean_context} testIdPrefix={`lead-tank-context-${lead.id}`} />
              </div>
            </section>
          )}

          {/* Status editor */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Update status</h3>
            <div>
              <label className="text-xs font-medium text-slate-600">Status</label>
              <select
                data-testid="partner-lead-status-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Your internal quote number (optional)</label>
                <Input
                  data-testid="partner-lead-quote-number"
                  value={quoteNumber}
                  onChange={(e) => setQuoteNumber(e.target.value)}
                  placeholder="Q-2026-0142"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Estimated quote value AUD (optional)</label>
                <div className="mt-1 relative">
                  <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="partner-lead-quote-value"
                    value={valueDollars}
                    onChange={(e) => setValueDollars(e.target.value.replace(/[^0-9.]/g, ""))}
                    placeholder="4250"
                    className="pl-8"
                    inputMode="decimal"
                  />
                </div>
                {nudgeQuoteValue && (
                  <p className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-1" data-testid="partner-lead-nudge-value">
                    <AlertCircle className="w-3 h-3" /> Adding a value helps AWARE track your pipeline (still optional).
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Notes (private — AWARE staff will not see partner-specific detail)</label>
              <textarea
                data-testid="partner-lead-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
                placeholder="Any internal notes about this customer"
                className="mt-1 w-full min-h-[80px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                maxLength={2000}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Outcome / status change note (visible in audit log)</label>
              <Input
                data-testid="partner-lead-history-note"
                value={historyNote}
                onChange={(e) => setHistoryNote(e.target.value)}
                placeholder="Called and left voicemail"
                className="mt-1"
              />
            </div>

            {["accepted","declined","completed"].includes(status) && (
              <div>
                <label className="text-xs font-medium text-slate-600">Outcome summary (optional, short)</label>
                <Input
                  data-testid="partner-lead-outcome"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value.slice(0, 120))}
                  placeholder="Installed UV + QuadClean; scheduled 6-month service"
                  className="mt-1"
                />
              </div>
            )}
          </section>

          {/* Status history */}
          {(lead.status_history || []).length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">History</h3>
              <ol className="space-y-2 text-xs">
                {lead.status_history.slice().reverse().map((h, i) => (
                  <li key={i} className="border-l-2 border-slate-200 pl-3 py-1">
                    <div className="flex items-center gap-2">
                      <StatusPill status={h.status} small />
                      <span className="text-slate-500">{fmtDateTime(h.at)}</span>
                    </div>
                    {h.note && <div className="text-slate-700 mt-0.5">{h.note}</div>}
                    <div className="text-slate-400 mt-0.5">{h.role} · {h.by}</div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Save */}
          <div className="pt-2 flex items-center gap-2">
            <Button
              data-testid="partner-lead-save-btn"
              onClick={onSave}
              disabled={saving}
              className="bg-slate-900 hover:bg-cyan-700 text-white"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save changes
            </Button>
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <div className="ml-auto text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Info className="w-3 h-3" />
              AWARE never sees your quote pricing
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
